import { useMutation } from '@tanstack/react-query';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';

interface ProgrammeSignatureTokenResponse {
	payload: {
		token: string;
	};
}

interface ProgrammeSignatureTokenRequest {
	signatureId: string;
}

/**
 * Exchanges a programme-scoped public signing token for a per-signature token,
 * authorised by ownership of `signatureId` within that programme (see backend
 * `PublicBulkSigningController#getSignatureToken`). Both the single-document
 * landing and the per-row "sign" fallback on `ProgrammeSigningPage` use this to
 * hand off to the existing single-document public signing flow
 * (`/public-signing?token=<perSignatureToken>`), which requires a
 * signature-scoped token rather than the programme token the signatory holds.
 *
 * The generated `apiPaths.ts` constant for this endpoint doesn't exist yet, so
 * the literal path is used here (mirrors the other `public-signing/programme/*`
 * hooks before their constants were generated).
 */
export const usePublicSignatureTokenExchange = (token: string) =>
	useMutation({
		mutationFn: async (body: ProgrammeSignatureTokenRequest) => {
			const response = await makeRequest<ProgrammeSignatureTokenResponse>(
				{
					endpoint: '/public-signing/programme/signature-token',
					method: HTTP_METHOD.POST,
					queryString: { token },
					body: JSON.stringify(body),
				},
				// Unauthenticated flow: a 401 (e.g. expired programme token) must
				// reach the caller's error handling, not redirect to the login page.
				{ skipLoginRedirect: true }
			);
			return response.payload;
		},
	});
